import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalanceWallet as CashIcon,
  AccountBalance as BankIcon,
  AttachMoney as FineIcon,
} from "@mui/icons-material";

const LedgerModal = ({
  isOpen,
  onClose,
  customer,
  fetchCustomerTransactions,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [loading, setLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState("cash");
  const [ledgerData, setLedgerData] = useState({
    rows: [],
    cash: {
      totalCredit: 0,
      totalDebit: 0,
      finalBalance: 0,
      openingBalance: 0,
      transactions: [],
    },
    bank: {
      totalCredit: 0,
      totalDebit: 0,
      finalBalance: 0,
      openingBalance: 0,
      transactions: [],
    },
    fine: {
      totalCredit: 0,
      totalDebit: 0,
      finalBalance: 0,
      openingBalance: 0,
      transactions: [],
    },
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const calculateLedger = useCallback((txns) => {
    // Initialize with opening balances
    let cashBalance = customer?.openingBalanceCash || 0;
    let bankBalance = customer?.openingBalanceBank || 0;
    let fineBalance = customer?.openingBalanceFine || 0;

    let cashCredit = 0;
    let cashDebit = 0;
    let bankCredit = 0;
    let bankDebit = 0;
    let fineCredit = 0;
    let fineDebit = 0;

    // Arrays to store individual transactions for each category
    const cashTransactions = [];
    const bankTransactions = [];
    const fineTransactions = [];

    // Track running balances for each category
    let runningCashBalance = cashBalance;
    let runningBankBalance = bankBalance;
    let runningFineBalance = fineBalance;

    txns.forEach((t) => {
      const grandTotal = parseFloat(t?.summary?.grandTotal) || 0;
      const isGstApplicable = t?.transactionDetails?.gstApplicable === true;
      const paymentHistory = t?.paymentDetails || [];
      const calculatedTotals = t?.calculatedTotals || [];

      // Calculate payments
      let cashPayment = parseFloat(paymentHistory.cash) || 0;
      let bankPayment = (parseFloat(paymentHistory.online) || 0) + (parseFloat(paymentHistory.cheque) || 0);
      let finePayment = parseFloat(calculatedTotals?.finePaymentReceived) || 0;

      // Credit amounts
      let cashCreditAmount = 0;
      let bankCreditAmount = 0;
      let fineCreditAmount = parseFloat(calculatedTotals?.totalFineWeight) || 0;

      if (isGstApplicable) {
        bankCreditAmount = grandTotal;
      } else {
        cashCreditAmount = grandTotal;
      }

      // Debit amounts
      let cashDebitAmount = cashPayment;
      let bankDebitAmount = bankPayment;
      let fineDebitAmount = finePayment;

      // Update running balances
      runningCashBalance = runningCashBalance + cashCreditAmount - cashDebitAmount;
      runningBankBalance = runningBankBalance + bankCreditAmount - bankDebitAmount;
      runningFineBalance = runningFineBalance + fineCreditAmount - fineDebitAmount;

      // Update totals
      cashCredit += cashCreditAmount;
      cashDebit += cashDebitAmount;
      bankCredit += bankCreditAmount;
      bankDebit += bankDebitAmount;
      fineCredit += fineCreditAmount;
      fineDebit += fineDebitAmount;

      const billNo = t?.transactionDetails?.billNo || '-';
      const date = t?.Date || '-';
      const type = isGstApplicable ? "Tax Invoice" : "Cash Memo";

      // Store transaction in respective category if it has any movement
      if (cashCreditAmount > 0 || cashDebitAmount > 0) {
        cashTransactions.push({
          billNo,
          date,
          type,
          credit: cashCreditAmount,
          debit: cashDebitAmount,
          balance: runningCashBalance,
          gstApplicable: isGstApplicable,
        });
      }

      if (bankCreditAmount > 0 || bankDebitAmount > 0) {
        bankTransactions.push({
          billNo,
          date,
          type,
          credit: bankCreditAmount,
          debit: bankDebitAmount,
          balance: runningBankBalance,
          gstApplicable: isGstApplicable,
        });
      }

      if (fineCreditAmount > 0 || fineDebitAmount > 0) {
        fineTransactions.push({
          billNo,
          date,
          type,
          credit: fineCreditAmount,
          debit: fineDebitAmount,
          balance: runningFineBalance,
          gstApplicable: isGstApplicable,
        });
      }
    });

    setLedgerData({
      rows: txns.map((t) => ({
        billNo: t?.transactionDetails?.billNo || '-',
        date: t?.Date || '-',
        type: t?.transactionDetails?.gstApplicable ? "Tax Invoice" : "Cash Memo",
        gstApplicable: t?.transactionDetails?.gstApplicable === true,
      })),
      cash: {
        totalCredit: cashCredit.toFixed(2),
        totalDebit: cashDebit.toFixed(2),
        finalBalance: runningCashBalance.toFixed(2),
        openingBalance: customer?.openingBalanceCash || 0,
        transactions: cashTransactions,
      },
      bank: {
        totalCredit: bankCredit.toFixed(2),
        totalDebit: bankDebit.toFixed(2),
        finalBalance: runningBankBalance.toFixed(2),
        openingBalance: customer?.openingBalanceBank || 0,
        transactions: bankTransactions,
      },
      fine: {
        totalCredit: fineCredit.toFixed(2),
        totalDebit: fineDebit.toFixed(2),
        finalBalance: runningFineBalance.toFixed(2),
        openingBalance: customer?.openingBalanceFine || 0,
        transactions: fineTransactions,
      },
    });
  }, [customer]);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCustomerTransactions(customer.id);
      const txns = data || [];
      calculateLedger(txns);
    } catch (error) {
      console.error("Failed to load transactions:", error);
      calculateLedger([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.id, fetchCustomerTransactions, calculateLedger]);

  useEffect(() => {
    if (isOpen && customer?.id) {
      loadLedger();
    }
  }, [isOpen, customer?.id, loadLedger]);

  if (!isOpen) return null;

  const getBalanceColor = (balance) => {
    const numBalance = parseFloat(balance);
    if (numBalance > 0) return isDark ? "#4ade80" : "#16a34a";
    if (numBalance < 0) return isDark ? "#f87171" : "#dc2626";
    return isDark ? "#a0a0a0" : "#6b7280";
  };

  const getStatusChip = (balance) => {
    const numBalance = parseFloat(balance);
    if (numBalance > 0) return <Chip label="Credit" size="small" sx={{ bgcolor: '#4ade80', color: '#000' }} />;
    if (numBalance < 0) return <Chip label="Debit" size="small" sx={{ bgcolor: '#f87171', color: '#000' }} />;
    return <Chip label="Zero" size="small" sx={{ bgcolor: '#6b7280', color: '#fff' }} />;
  };

  const TransactionTable = ({ transactions, openingBalance, type, showFine = false }) => {
    if (transactions.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography sx={{ color: isDark ? "#a0a0a0" : "#6b7280", fontSize: "14px" }}>
            No transactions found for {type} balance
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
            "& th, & td": {
              p: "8px 12px",
              textAlign: "right",
            },
            "& th:first-of-type, & td:first-of-type": {
              textAlign: "left",
            },
            "& thead": {
              background: isDark
                ? "linear-gradient(120deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))"
                : "linear-gradient(120deg, rgba(239, 246, 255, 0.98), rgba(224, 231, 255, 0.98))",
            },
            "& th": {
              fontWeight: 600,
              color: isDark ? "#e0e0e0" : "#4b5563",
              borderBottom: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(226, 232, 240, 0.9)",
            },
            "& tbody tr:nth-of-type(even)": {
              background: isDark ? "rgba(50, 50, 50, 0.5)" : "#f9fafb",
            },
            "& tbody tr:hover": {
              background: isDark ? "rgba(37, 99, 235, 0.1)" : "#eef4ff",
            },
            "& td": {
              color: isDark ? "#e0e0e0" : "#1a1a1a",
            },
            "& .credit": {
              color: "#4ade80",
              fontWeight: 600,
            },
            "& .debit": {
              color: "#f87171",
              fontWeight: 600,
            },
            "& .gst-tag": {
              display: "inline-block",
              padding: "1px 6px",
              borderRadius: "4px",
              fontSize: "9px",
              fontWeight: 600,
              background: isDark ? "rgba(37, 99, 235, 0.2)" : "rgba(37, 99, 235, 0.1)",
              color: "#3b82f6",
              marginLeft: "4px",
            },
          }}
        >
          <thead>
            <tr>
              <th style={{ minWidth: "80px" }}>Bill No.</th>
              <th style={{ minWidth: "60px" }}>Type</th>
              <th style={{ minWidth: "80px" }}>Credit</th>
              <th style={{ minWidth: "80px" }}>Debit</th>
              <th style={{ minWidth: "80px" }}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {console.log("Transactions for type", type, transactions)}
            {/* Opening Balance Row */}
            <tr style={{ fontWeight: 600, background: isDark ? "rgba(50, 50, 50, 0.8)" : "#f3f4f6" }}>
              <td>Opening</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>{type === "Fine" ? `${openingBalance?.toFixed(2) || "0.00"} gms` : `₹${openingBalance?.toFixed(2) || "0.00"}`}</td>
            </tr>

            {/* Transaction Rows */}
            {transactions.map((row, index) => (
              <tr key={index}>
                <td>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {row.billNo}
                    {row.gstApplicable && <span className="gst-tag">GST</span>}
                  </Box>
                </td>
                <td>{row.type}</td>
                <td className="credit">{type === "Fine" ? `${row.credit.toFixed(2)} gms` : `₹${row.credit.toFixed(2)}`}</td>
                <td className="debit">{type === "Fine" ? `${row.debit.toFixed(2)} gms` : `₹${row.debit.toFixed(2)}`}</td>
                <td style={{ fontWeight: 600, color: getBalanceColor(row.balance) }}>
                  {type === "Fine" ? `${row.balance.toFixed(2)} gms` : `₹${row.balance.toFixed(2)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    );
  };

  const BalanceSummaryCard = ({ title, data, icon, color, type }) => {
    const isFine = type === "fine";
    const balanceValue = parseFloat(data.finalBalance);
    const openingValue = parseFloat(data.openingBalance);

    return (
      <Card
        elevation={0}
        sx={{
          background: isDark
            ? "rgba(40, 40, 40, 0.96)"
            : "rgba(255, 255, 255, 0.96)",
          borderRadius: "12px",
          border: isDark
            ? `1px solid ${alpha(color, 0.3)}`
            : `1px solid ${alpha(color, 0.2)}`,
          mb: 1,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: alpha(color, 0.1),
                }}
              >
                {icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: isDark ? "#e0e0e0" : "#1a1a1a",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: isDark ? "#a0a0a0" : "#6b7280",
                  }}
                >
                  Opening: {isFine ? `${openingValue.toFixed(2)} gms` : `₹${openingValue.toFixed(2)}`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: getBalanceColor(balanceValue),
                }}
              >
                {isFine ? `${balanceValue.toFixed(2)} gms` : `₹${balanceValue.toFixed(2)}`}
              </Typography>
              {getStatusChip(balanceValue)}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: isDark
            ? "rgba(30, 30, 30, 0.95)"
            : "rgba(255, 255, 255, 0.9)",
          borderRadius: "24px",
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(148, 163, 184, 0.6)",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(20px)",
          maxWidth: "1000px",
          width: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          p: "18px 20px 16px",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          background: "rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "10px",
          mb: "10px",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: isDark ? "#e0e0e0" : "#020617",
              m: 0,
            }}
          >
            Customer Ledger
          </Typography>
          <Typography
            sx={{
              mt: "2px",
              fontSize: "13px",
              color: isDark ? "#a0a0a0" : "#64748b",
            }}
          >
            {customer?.name} - Detailed statement showing Cash, Bank, and Fine balances separately.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            border: "none",
            background: isDark
              ? "rgba(50, 50, 50, 0.96)"
              : "rgba(248, 250, 252, 0.96)",
            borderRadius: "999px",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isDark ? "#a0a0a0" : "#475569",
            boxShadow: "0 8px 18px rgba(148, 163, 184, 0.4)",
            transition: "background 0.15s ease, transform 0.08s ease",
            "&:hover": {
              background: "#fee2e2",
              color: "#b91c1c",
              transform: "translateY(-1px)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          overflow: "hidden",
          flex: 1,
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={40} sx={{ color: "#2563eb", mb: 2 }} />
            <Typography sx={{ color: isDark ? "#a0a0a0" : "#6b7280" }}>
              Loading ledger...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={4}>
                <BalanceSummaryCard
                  title="Cash Balance"
                  data={ledgerData.cash}
                  color="#16a34a"
                  type="cash"
                  icon={<CashIcon sx={{ color: "#16a34a", fontSize: 20 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BalanceSummaryCard
                  title="Bank Balance"
                  data={ledgerData.bank}
                  color="#2563eb"
                  type="bank"
                  icon={<BankIcon sx={{ color: "#2563eb", fontSize: 20 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BalanceSummaryCard
                  title="Fine Balance"
                  data={ledgerData.fine}
                  color="#f59e0b"
                  type="fine"
                  icon={<FineIcon sx={{ color: "#f59e0b", fontSize: 20 }} />}
                />
              </Grid>
            </Grid>

            {/* Accordion Sections */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {/* Cash Accordion */}
              <Accordion
                expanded={expandedSection === "cash"}
                onChange={handleAccordionChange("cash")}
                sx={{
                  background: isDark
                    ? "rgba(40, 40, 40, 0.96)"
                    : "rgba(255, 255, 255, 0.96)",
                  borderRadius: "12px !important",
                  mb: 1,
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(226, 232, 240, 0.9)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#e0e0e0" : "#1a1a1a" }} />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 2,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CashIcon sx={{ color: "#16a34a" }} />
                    <Typography sx={{ fontWeight: 600, color: isDark ? "#e0e0e0" : "#1a1a1a" }}>
                      Cash Balance
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
                    <Chip
                      label={`Credit: ₹${ledgerData.cash.totalCredit}`}
                      size="small"
                      sx={{ bgcolor: alpha("#4ade80", 0.2), color: "#4ade80" }}
                    />
                    <Chip
                      label={`Debit: ₹${ledgerData.cash.totalDebit}`}
                      size="small"
                      sx={{ bgcolor: alpha("#f87171", 0.2), color: "#f87171" }}
                    />
                    <Chip
                      label={`Balance: ₹${ledgerData.cash.finalBalance}`}
                      size="small"
                      sx={{ bgcolor: getBalanceColor(parseFloat(ledgerData.cash.finalBalance)), color: "#fff" }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TransactionTable
                    transactions={ledgerData.cash.transactions}
                    openingBalance={ledgerData.cash.openingBalance}
                    type="Cash"
                  />
                </AccordionDetails>
              </Accordion>

              {/* Bank Accordion */}
              <Accordion
                expanded={expandedSection === "bank"}
                onChange={handleAccordionChange("bank")}
                sx={{
                  background: isDark
                    ? "rgba(40, 40, 40, 0.96)"
                    : "rgba(255, 255, 255, 0.96)",
                  borderRadius: "12px !important",
                  mb: 1,
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(226, 232, 240, 0.9)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#e0e0e0" : "#1a1a1a" }} />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 2,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <BankIcon sx={{ color: "#2563eb" }} />
                    <Typography sx={{ fontWeight: 600, color: isDark ? "#e0e0e0" : "#1a1a1a" }}>
                      Bank Balance
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
                    <Chip
                      label={`Credit: ₹${ledgerData.bank.totalCredit}`}
                      size="small"
                      sx={{ bgcolor: alpha("#4ade80", 0.2), color: "#4ade80" }}
                    />
                    <Chip
                      label={`Debit: ₹${ledgerData.bank.totalDebit}`}
                      size="small"
                      sx={{ bgcolor: alpha("#f87171", 0.2), color: "#f87171" }}
                    />
                    <Chip
                      label={`Balance: ₹${ledgerData.bank.finalBalance}`}
                      size="small"
                      sx={{ bgcolor: getBalanceColor(parseFloat(ledgerData.bank.finalBalance)), color: "#fff" }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TransactionTable
                    transactions={ledgerData.bank.transactions}
                    openingBalance={ledgerData.bank.openingBalance}
                    type="Bank"
                  />
                </AccordionDetails>
              </Accordion>

              {/* Fine Accordion */}
              <Accordion
                expanded={expandedSection === "fine"}
                onChange={handleAccordionChange("fine")}
                sx={{
                  background: isDark
                    ? "rgba(40, 40, 40, 0.96)"
                    : "rgba(255, 255, 255, 0.96)",
                  borderRadius: "12px !important",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(226, 232, 240, 0.9)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#e0e0e0" : "#1a1a1a" }} />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 2,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FineIcon sx={{ color: "#f59e0b" }} />
                    <Typography sx={{ fontWeight: 600, color: isDark ? "#e0e0e0" : "#1a1a1a" }}>
                      Fine Balance
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
                    <Chip
                      label={`Credit: ${ledgerData.fine.totalCredit} gms`}
                      size="small"
                      sx={{ bgcolor: alpha("#4ade80", 0.2), color: "#4ade80" }}
                    />
                    <Chip
                      label={`Debit: ${ledgerData.fine.totalDebit} gms`}
                      size="small"
                      sx={{ bgcolor: alpha("#f87171", 0.2), color: "#f87171" }}
                    />
                    <Chip
                      label={`Balance: ${ledgerData.fine.finalBalance} gms`}
                      size="small"
                      sx={{ bgcolor: getBalanceColor(parseFloat(ledgerData.fine.finalBalance)), color: "#fff" }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TransactionTable
                    transactions={ledgerData.fine.transactions}
                    openingBalance={ledgerData.fine.openingBalance}
                    type="Fine"
                    showFine={true}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default LedgerModal;